import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "orthodontist-web-design-colorado",
  metaTitle: "Orthodontist Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom orthodontist web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Dental marketing", to: "/dental-marketing-colorado" },
  ],
  currentLabel: "Orthodontist Web Design Colorado",
  eyebrow: "Orthodontist Web Design — Colorado",
  h1: (
    <>
      Orthodontist <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded orthodontist websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Orthodontist websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first orthodontist sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Orthodontist web design in Colorado",
    body: "Orthodontist web design is the practice of building conversion-focused, mobile-first websites for orthodontist businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with free consult booking above the fold." },
    { title: "Treatment service pages", body: "Braces, Invisalign, clear aligners, early treatment, adult ortho, retainers — each ranks." },
    { title: "Free consultation booking", body: "Real-time booking integration with Cloud 9, Dolphin, or your PMS." },
    { title: "Before/after galleries", body: "Properly consented gallery with image schema and lazy loading." },
    { title: "Provider bio pages", body: "Each orthodontist with credentials, training, and direct booking CTA." },
    { title: "Schema and SEO at launch", body: "Dentist, MedicalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Free consult is the conversion engine", body: "Above-the-fold free consult CTA drives 3x more starts than generic contact forms." },
    { heading: "Invisalign vs braces are different searches", body: "Adult patients search Invisalign, parents search braces. Build for both." },
    { heading: "Visual proof + financing close the start", body: "Before/after galleries + monthly payment options surfaced above the fold close the case." },
  ],
  relatedLinks: [
    { to: "/dental-marketing-colorado", label: "Dental marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a orthodontist website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a orthodontist website?", a: "Typical timeline is 4–6 weeks for a 12–20 page orthodontist site with full service and service-area pages. Larger multi-location orthodontist businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs orthodontist businesses?", a: "No — we build orthodontist sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Orthodontist Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for orthodontist businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/orthodontist-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
